/**
 * Sleep view for the Trends "Sleep" tab — a different visualization from the
 * metric line charts: a nightly stacked-duration bar (by stage when present),
 * an average-duration readout, and a bedtime-consistency scatter strip. Data
 * comes from /api/sleep; the sessions→rows math lives in ../lib/sleepChart.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { init, use, type ECharts, type ComposeOption } from 'echarts/core';
import { BarChart, ScatterChart, type BarSeriesOption, type ScatterSeriesOption } from 'echarts/charts';
import {
  GridComponent,
  LegendComponent,
  TooltipComponent,
  MarkLineComponent,
  type GridComponentOption,
  type LegendComponentOption,
  type TooltipComponentOption,
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import { getSleep, getLatestSleep, type SleepSession } from '../api.js';
import { slotColor } from '../metrics.js';
import { useThemeMode } from '../lib/useThemeMode.js';
import { toSleepNights, avgDurationHours, type SleepNight } from '../lib/sleepChart.js';
import { lastNDays, nDaysEnding } from '../lib/range.js';
import { fmtDuration, fmtYmd } from '../lib/format.js';
import { Button, Card, EmptyState, Segmented } from '../ui/index.js';

use([
  BarChart,
  ScatterChart,
  GridComponent,
  LegendComponent,
  TooltipComponent,
  MarkLineComponent,
  CanvasRenderer,
]);

type ChartOption = ComposeOption<
  | BarSeriesOption
  | ScatterSeriesOption
  | GridComponentOption
  | LegendComponentOption
  | TooltipComponentOption
>;

const STAGE_ORDER = ['deep', 'core', 'rem', 'awake'] as const;
const STAGE_LABEL: Record<string, string> = { deep: 'Deep', core: 'Core', rem: 'REM', awake: 'Awake' };
// Reuse validated categorical slots so stage colors are CVD-safe: deep=indigo(5),
// core=blue(1), rem=green(2), awake=amber(3).
const STAGE_SLOT: Record<string, number> = { deep: 5, core: 1, rem: 2, awake: 3 };

function chrome(mode: 'light' | 'dark') {
  return mode === 'dark'
    ? { text: '#ffffff', muted: 'rgba(235,235,245,0.6)', grid: 'rgba(84,84,88,0.45)', axis: 'rgba(84,84,88,0.65)', surface: '#1c1c1e' }
    : { text: '#000000', muted: 'rgba(60,60,67,0.6)', grid: 'rgba(60,60,67,0.12)', axis: 'rgba(60,60,67,0.29)', surface: '#ffffff' };
}

function fmtDay(date: string): string {
  const [, m, d] = date.split('-');
  return `${Number(m)}/${Number(d)}`;
}

/** hh:mm from a night-hour value (25.5 → "1:30a", 22 → "10:00p"). */
function fmtBedtime(hour: number): string {
  const h24 = ((Math.floor(hour) % 24) + 24) % 24;
  const min = Math.round((hour - Math.floor(hour)) * 60);
  const ampm = h24 < 12 ? 'a' : 'p';
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h12}:${String(min).padStart(2, '0')}${ampm}`;
}

function durationOption(nights: SleepNight[], mode: 'light' | 'dark'): ChartOption {
  const c = chrome(mode);
  const dates = nights.map((n) => n.date);
  const anyStages = nights.some((n) => n.stages !== null);

  const stageSeries: BarSeriesOption[] = STAGE_ORDER.map((stage) => ({
    name: STAGE_LABEL[stage],
    type: 'bar',
    stack: 'sleep',
    barMaxWidth: 18,
    itemStyle: { color: slotColor(STAGE_SLOT[stage]!, mode), borderColor: c.surface, borderWidth: 1, borderRadius: 3 },
    emphasis: { focus: 'series' },
    data: nights.map((n) => (n.stages ? Math.round(n.stages[stage] * 100) / 100 : 0)),
  }));

  // Nights without a stage breakdown contribute their total to an "Untracked"
  // segment so every night still shows its duration.
  const untracked: BarSeriesOption = {
    name: 'Untracked',
    type: 'bar',
    stack: 'sleep',
    barMaxWidth: 22,
    itemStyle: { color: c.muted, borderColor: c.surface, borderWidth: 1 },
    emphasis: { focus: 'series' },
    data: nights.map((n) => (n.stages ? 0 : Math.round(n.durationHours * 100) / 100)),
  };

  const avg = avgDurationHours(nights);

  return {
    grid: { left: 4, right: 8, top: 36, bottom: 4, containLabel: true },
    legend: {
      top: 0,
      left: 0,
      textStyle: { color: c.text, fontSize: 13 },
      icon: 'circle',
      itemWidth: 8,
      itemHeight: 8,
      itemGap: 14,
      data: [...(anyStages ? STAGE_ORDER.map((s) => STAGE_LABEL[s]!) : []), ...(anyStages ? [] : ['Untracked'])],
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      backgroundColor: mode === 'dark' ? '#2c2c2e' : '#ffffff',
      borderColor: 'transparent',
      borderWidth: 0,
      extraCssText: 'border-radius:12px;box-shadow:0 6px 24px rgba(0,0,0,.16);',
      textStyle: { color: c.text, fontSize: 13 },
      valueFormatter: (v) => (typeof v === 'number' && v > 0 ? `${v.toFixed(1)} h` : ''),
    },
    xAxis: {
      type: 'category',
      data: dates.map(fmtDay),
      axisLine: { lineStyle: { color: c.axis } },
      axisLabel: { color: c.muted, fontSize: 13, interval: 'auto', hideOverlap: true },
      axisTick: { show: false },
    },
    yAxis: {
      type: 'value',
      axisLabel: { color: c.muted, fontSize: 13, formatter: (v: number) => `${v}h` },
      splitLine: { lineStyle: { color: c.grid, type: [3, 3] } },
    },
    series: [
      ...(anyStages ? stageSeries : []),
      untracked,
      // Average line rides on the last series so it draws once.
      {
        name: 'avg',
        type: 'bar',
        stack: 'sleep',
        data: nights.map(() => 0),
        markLine: {
          silent: true,
          symbol: 'none',
          lineStyle: { color: c.text, type: 'dashed', width: 1 },
          label: { color: c.muted, fontSize: 13, position: 'insideEndTop', formatter: `avg ${avg.toFixed(1)}h` },
          data: [{ yAxis: Math.round(avg * 100) / 100 }],
        },
      } as BarSeriesOption,
    ],
  };
}

function bedtimeOption(nights: SleepNight[], mode: 'light' | 'dark'): ChartOption {
  const c = chrome(mode);
  const points = nights
    .map((n, i) => (n.bedtimeHour === null ? null : ([i, n.bedtimeHour] as [number, number])))
    .filter((p): p is [number, number] => p !== null);

  return {
    grid: { left: 4, right: 8, top: 12, bottom: 4, containLabel: true },
    tooltip: {
      trigger: 'item',
      backgroundColor: mode === 'dark' ? '#2c2c2e' : '#ffffff',
      borderColor: 'transparent',
      borderWidth: 0,
      extraCssText: 'border-radius:12px;box-shadow:0 6px 24px rgba(0,0,0,.16);',
      textStyle: { color: c.text, fontSize: 13 },
      formatter: (p: unknown) => {
        const d = (p as { data: [number, number] }).data;
        return `${nights[d[0]]?.date ?? ''} · ${fmtBedtime(d[1])}`;
      },
    },
    xAxis: {
      type: 'category',
      data: nights.map((n) => fmtDay(n.date)),
      axisLine: { lineStyle: { color: c.axis } },
      axisLabel: { color: c.muted, fontSize: 13, interval: 'auto', hideOverlap: true },
      axisTick: { show: false },
    },
    yAxis: {
      type: 'value',
      inverse: true, // earlier bedtime higher up
      scale: true, // fit to the actual bedtime band, don't span from 0
      axisLabel: { color: c.muted, fontSize: 13, formatter: (v: number) => fmtBedtime(v) },
      splitLine: { lineStyle: { color: c.grid, type: [3, 3] } },
    },
    series: [
      {
        type: 'scatter',
        symbolSize: 9,
        itemStyle: { color: slotColor(5, mode), borderColor: c.surface, borderWidth: 1 },
        data: points,
      },
    ],
  };
}

function useEChart(option: ChartOption | null): (el: HTMLDivElement | null) => void {
  const chartRef = useRef<ECharts | null>(null);
  const elRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = elRef.current;
    if (!el || !option) return;
    if (!chartRef.current || chartRef.current.isDisposed() || chartRef.current.getDom() !== el) {
      chartRef.current?.dispose();
      chartRef.current = init(el);
    }
    chartRef.current.setOption(option, true);
    const ro = new ResizeObserver(() => chartRef.current?.resize());
    ro.observe(el);
    return () => ro.disconnect();
  }, [option]);

  useEffect(() => () => {
    chartRef.current?.dispose();
    chartRef.current = null;
  }, []);

  return (el: HTMLDivElement | null) => {
    elRef.current = el;
  };
}

type LoadState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; nights: SleepNight[] };

const RANGES = [30, 90] as const;

/** Local calendar date (YYYY-MM-DD) of a session's bedtime, in the viewer's frame. */
function sessionLocalDate(session: SleepSession): string | null {
  const tz = -new Date().getTimezoneOffset();
  return toSleepNights([session], tz)[0]?.date ?? null;
}

export default function SleepView(): JSX.Element {
  const mode = useThemeMode();
  const [days, setDays] = useState<number>(30);
  // When set, the view shows the `days`-day window ENDING at this instant (a jump
  // to past data) instead of the trailing window ending now.
  const [anchorEnd, setAnchorEnd] = useState<string | null>(null);
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  // Newest recorded session — fetched once so an empty recent range can point the
  // user at where their data actually ends.
  const [latest, setLatest] = useState<SleepSession | null>(null);

  useEffect(() => {
    let alive = true;
    setState({ status: 'loading' });
    const { from, to } = anchorEnd ? nDaysEnding(days, new Date(anchorEnd)) : lastNDays(days);
    getSleep(from, to)
      .then((sessions) => {
        if (!alive) return;
        const tz = -new Date().getTimezoneOffset();
        setState({ status: 'ready', nights: toSleepNights(sessions, tz) });
      })
      .catch((err: unknown) => {
        if (!alive) return;
        setState({ status: 'error', message: err instanceof Error ? err.message : 'Failed to load sleep.' });
      });
    return () => {
      alive = false;
    };
  }, [days, anchorEnd]);

  useEffect(() => {
    let alive = true;
    getLatestSleep()
      .then((s) => {
        if (alive) setLatest(s);
      })
      .catch(() => {
        /* non-fatal: the empty-state hint just won't appear */
      });
    return () => {
      alive = false;
    };
  }, []);

  const latestDate = latest ? sessionLocalDate(latest) : null;
  const showLatest = (): void => {
    if (!latest) return;
    setDays(90);
    setAnchorEnd(latest.startedAt);
  };
  const backToRecent = (): void => setAnchorEnd(null);

  const nights = state.status === 'ready' ? state.nights : [];
  const durOpt = useMemo(() => (nights.length ? durationOption(nights, mode) : null), [nights, mode]);
  const bedOpt = useMemo(() => (nights.length ? bedtimeOption(nights, mode) : null), [nights, mode]);
  const durRef = useEChart(durOpt);
  const bedRef = useEChart(bedOpt);
  const avg = avgDurationHours(nights);

  return (
    <section className="hb-sleep">
      <div className="hb-seg">
        <Segmented
          label="Sleep range"
          value={String(days)}
          options={RANGES.map((n) => ({ value: String(n), label: `${n} days` }))}
          onChange={(v) => setDays(Number(v))}
        />
      </div>

      {anchorEnd && latestDate && (
        <div className="hb-sleep__anchor">
          <span>Showing {days} days through {fmtYmd(latestDate)}</span>
          <Button variant="plain" size="sm" onClick={backToRecent}>
            Back to recent
          </Button>
        </div>
      )}

      {state.status === 'error' && (
        <div className="state state--error">
          <div className="state__title">Couldn't load sleep</div>
          <div className="state__hint">{state.message}</div>
        </div>
      )}

      {state.status === 'ready' && nights.length === 0 &&
        (!anchorEnd && latestDate ? (
          <EmptyState
            icon="moon"
            title="No sleep in this range"
            hint={`Latest sleep data is from ${fmtYmd(latestDate)}.`}
            action={
              <Button size="sm" onClick={showLatest}>
                Show latest
              </Button>
            }
          />
        ) : (
          <EmptyState icon="moon" title="No sleep recorded" hint="Nothing logged in this range yet." />
        ))}

      {state.status === 'ready' && nights.length > 0 && (
        <>
          <Card className="hb-sleep__hero" label="Average night">
            <div className="hb-hero">
              <div className="hb-hero__text">
                <span className="hb-hero__label">Average night</span>
                <span className="hb-hero__value">{fmtDuration(Math.round(avg * 3600))}</span>
                <span className="hb-hero__sub">
                  over {nights.length} night{nights.length === 1 ? '' : 's'}
                </span>
              </div>
            </div>
          </Card>
          <section className="hb-chart" aria-label="Nightly duration by stage">
            <div className="hb-chart__head">
              <h2 className="hb-chart__title">Nightly duration</h2>
            </div>
            <div ref={durRef} className="sleep-chart" role="img" aria-label="Nightly sleep duration by stage" />
          </section>
          <section className="hb-chart" aria-label="Bedtime consistency">
            <div className="hb-chart__head">
              <h2 className="hb-chart__title">Bedtime</h2>
              <span className="hb-chart__sub">earlier is higher</span>
            </div>
            <div ref={bedRef} className="sleep-chart sleep-chart--short" role="img" aria-label="Bedtime by night" />
          </section>
        </>
      )}
    </section>
  );
}
